import { EnhancedAddAssetDialog } from "@/components/portfolio/EnhancedAddAssetDialog";
import { AllocationItem } from "./AllocationItem";
import { memo } from "react";

interface AssetClassContentProps {
  allocations: any[];
  assetClassTotal: number;
  assetClassDollarTotal: number;
  portfolioId: string;
  portfolioSize: number;
  isPortfolioSizeLocked: boolean;
  onAssetAdded: () => void;
  onDelete: (id: string) => void;
  onUpdatePercentage: (id: string, value: number) => void;
  onUpdateDollarAmount: (allocation: any, value: number) => void;
  onSuccess: () => void;
  initialFundType: string;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  // Optional editing mode, forwarded by parent but not used here
  mode?: 'percentage' | 'dollar';
  // Optimistic subtotal updates
  onLocalPercentageChange?: (allocationId: string, newPct: number) => void;
  onLocalDollarChange?: (allocationId: string, newDollars: number) => void;
  // Optimistic overrides for row display: allocationId -> percentage
  overrides?: Record<string, number>;
  // Display currency code for symbol rendering
  currencyCode?: string;
}

export const AssetClassContent = memo(({
  allocations,
  portfolioId,
  portfolioSize,
  isPortfolioSizeLocked,
  onAssetAdded,
  onDelete,
  onUpdatePercentage,
  onUpdateDollarAmount,
  onSuccess,
  initialFundType,
  selectedIds,
  onToggleSelect,
  mode,
  onLocalPercentageChange,
  onLocalDollarChange,
  overrides,
  currencyCode,
}: AssetClassContentProps) => {
  const effectiveMode: 'percentage' | 'dollar' = mode ?? 'percentage';
  return (
    <div className="space-y-4">
      {(allocations?.length || 0) > 0 && (
        <div className="space-y-2">
          {allocations.map((allocation) => {
            const ov = overrides?.[allocation.id];
            let displayAllocation = allocation;
            if (ov !== undefined) {
              if (effectiveMode === 'percentage') {
                displayAllocation = {
                  ...allocation,
                  allocation_percentage: ov,
                };
              } else {
                const val = Math.round(((ov / 100) * portfolioSize) * 100) / 100;
                displayAllocation = {
                  ...allocation,
                  allocation_percentage: ov,
                  allocation_value: val,
                  is_value_locked: true,
                };
              }
            }
            return (
              <AllocationItem
                key={allocation.id}
                allocation={displayAllocation}
                portfolioSize={portfolioSize}
                isPortfolioSizeLocked={isPortfolioSizeLocked}
                onDelete={onDelete}
                onUpdatePercentage={onUpdatePercentage}  
                onUpdateDollarAmount={onUpdateDollarAmount}
                onSuccess={onSuccess}
                selected={selectedIds?.has(allocation.id)}
                onToggleSelect={onToggleSelect}
                mode={mode}
                onLocalPercentageChange={onLocalPercentageChange}
                onLocalDollarChange={onLocalDollarChange}
                currencyCode={currencyCode}
              />
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2">
        <EnhancedAddAssetDialog
          portfolioId={portfolioId}
          onAssetAdded={onAssetAdded}
          defaultAssetType={initialFundType}
          buttonLabel="Add Asset"
        />
      </div>
    </div>
  );
});

AssetClassContent.displayName = 'AssetClassContent';
